import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import LanguageToggle from "../components/app/LanguageToggle";
import { Circle } from "lucide-react";
import Logo from "../components/app/Logo";

export default function Login() {
  const { login, formatError } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const next = sp.get("next");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email, password);
      nav(next && next.startsWith("/") && !next.startsWith("//") ? next : "/");
    } catch (e2) {
      setErr(formatError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-obsidian text-white">
      {/* Left: form */}
      <div className="flex flex-col p-8 md:p-16 justify-between">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand">
            <Circle className="w-5 h-5 text-pitch hidden" fill="#3B82F6" />
            <Logo size={28} wordmark />
          </Link>
          <LanguageToggle />
        </div>

        <div className="my-12 max-w-md w-full">
          <div className="label-mono mb-3">{t("elite")}</div>
          <h1 className="font-oswald uppercase text-4xl sm:text-5xl tracking-tight mb-2">
            {t("login")}
          </h1>
          <p className="text-white/50 mb-8">{t("loginToContinue")}</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="label-mono block mb-1.5">{t("email")}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full bg-[#0A1628] border border-white/10 px-3 py-2.5 outline-none focus:border-pitch transition-colors"
                data-testid="login-email"
              />
            </div>
            <div>
              <label className="label-mono block mb-1.5">{t("password")}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full bg-[#0A1628] border border-white/10 px-3 py-2.5 outline-none focus:border-pitch transition-colors"
                data-testid="login-password"
              />
            </div>

            {err && <div className="text-red-400 text-sm font-mono" data-testid="login-error">{err}</div>}

            <button type="submit" disabled={busy} className="w-full btn-primary py-3" data-testid="login-submit">
              {busy ? "..." : t("login")}
            </button>
          </form>

          <div className="mt-6 text-sm text-white/50">
            {t("noAccount")}{" "}
            <Link to="/register" className="text-pitch hover:underline" data-testid="link-register">
              {t("register")}
            </Link>
          </div>
        </div>

        <div className="label-mono text-white/30">© ONTHEPITCH — {t("tagline")}</div>
      </div>

      {/* Right: image panel */}
      <div className="hidden md:block relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1517747614396-d21a78b850e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwzfHxmb290YmFsbCUyMHBpdGNoJTIwZGFyayUyMHRleHR1cmV8ZW58MHx8fHwxNzc4ODU1OTExfDA&ixlib=rb-4.1.0&q=85"
          alt="pitch"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-obsidian via-obsidian/60 to-obsidian/30" />
        <div className="absolute inset-0 pitch-pattern opacity-30" />
        <div className="absolute bottom-12 right-12 left-12">
          <div className="label-mono mb-2 text-pitch">// COMMAND CENTER</div>
          <div className="font-oswald uppercase text-5xl leading-none">{t("builtFor")}</div>
        </div>
      </div>
    </div>
  );
}
